import React, { useEffect, useState, useCallback } from 'react';
import { ShieldAlert, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { apiCall } from '../../utils/api';
import { toast } from '../ui/sonner';

/**
 * SupportRequestPrompt
 * --------------------
 * Polls `/api/employer/support-mode/pending` for any pending admin
 * support-mode consent requests aimed at this employer/agent account.
 * When one exists, renders a modal so the org owner can Approve or
 * Deny with an optional note. Approving unlocks the admin's session.
 */
export default function SupportRequestPrompt() {
  const [pending, setPending] = useState([]);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await apiCall('/api/employer/support-mode/pending');
      setPending(r.requests || []);
    } catch {
      /* silent — banner just won't appear */
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const decide = async (session, decision) => {
    setBusy(session._id + decision);
    try {
      await apiCall(`/api/employer/support-mode/${session._id}/decide`, {
        method: 'POST',
        body: JSON.stringify({ decision, note }),
      });
      toast.success(
        decision === 'approve'
          ? 'Support access granted — the admin can now act on your account.'
          : 'Request denied.'
      );
      setNote('');
      await load();
    } catch (e) {
      toast.error(e?.message || 'Could not save decision');
    }
    setBusy(null);
  };

  if (!pending.length) return null;
  const s = pending[0];

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      data-testid="support-request-prompt"
    >
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl overflow-hidden">
        <div className="bg-gradient-to-r from-rose-600 to-orange-600 text-white px-5 py-4 flex items-start gap-3">
          <ShieldAlert className="h-6 w-6 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-lg font-bold">Support access request</h3>
            <p className="text-sm text-white/90">
              A platform administrator is asking to enter your account so they can help troubleshoot.
              Nothing changes on your side until you approve.
            </p>
          </div>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
            <p className="font-semibold text-slate-900">{s.admin_name || s.admin_email}</p>
            <p className="text-xs text-slate-500">{s.admin_email}</p>
            {s.reason && (
              <p className="mt-2 text-slate-700 whitespace-pre-line">
                <span className="text-xs uppercase tracking-wide text-slate-400 font-semibold block">Reason</span>
                {s.reason}
              </p>
            )}
            <p className="mt-2 text-slate-700">
              <span className="text-xs uppercase tracking-wide text-slate-400 font-semibold block">Requested access to</span>
              {(s.scopes || ['all']).includes('all')
                ? 'Full account access'
                : (s.scopes || []).join(', ')}
            </p>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">
              Note back to the admin (optional)
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              maxLength={500}
              placeholder="e.g. Please only touch the payments tab."
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
              data-testid="support-request-note"
            />
          </div>
          <p className="text-[11px] text-slate-500">
            If you approve, the admin gets a 4-hour window and every action they take is written to the audit log.
          </p>
        </div>
        <div className="border-t border-slate-200 px-5 py-3 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => decide(s, 'deny')}
            disabled={busy !== null}
            className="inline-flex items-center gap-1.5 rounded-lg border border-red-300 bg-white px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
            data-testid="support-request-deny"
          >
            {busy === s._id + 'deny' ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />} Deny
          </button>
          <button
            type="button"
            onClick={() => decide(s, 'approve')}
            disabled={busy !== null}
            className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
            data-testid="support-request-approve"
          >
            {busy === s._id + 'approve' ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Approve
          </button>
        </div>
      </div>
    </div>
  );
}
